import React, { useEffect, useState } from 'react';
import ReactHtmlParser from 'react-html-parser';
import './TermDictionaries.css';
import {
    containsEnglish,
    containsTibetan,
    convertHiToHTML,
    escapeStrayAngleBrackets,
    getDictSources,
    getLangClass,
    getRelatedProps,
    getUniquePropIds,
    markTibetan,
} from '../../common/utils';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useQuery } from 'react-query';
import { useDictionaryStore } from '../../../hooks/useDictionaryStore';
import { BsInfoCircle, HiInformationCircle } from 'react-icons/all';
import { MandalaPopover } from '../../common/MandalaPopover';
import { km } from 'date-fns/locale';
import { getLangCodeForString } from '../../../catalog/common/utils';
import { dictTitleFix, TermDictionarySource } from './TermDictionaryChooser';
import { GlossaryTermDisplay } from '../TermGlossary/TermsGlossaryEntry';

/**
 * Kmaps Terms Api that returns order of dictionaries to display
 * @returns {Promise<any>}
 */
export const getDictionaryOrder = async (format = 'object') => {
    const apiurl = process.env?.REACT_APP_DICTIONARY_LIST;
    try {
        const { data } = await axios.request(apiurl);
        const dictdict = {};
        if (Array.isArray(data?.info_sources) && data.info_sources.length > 0) {
            if (format === 'list') {
                return data?.info_sources;
            }
            data.info_sources.forEach((item) => {
                if (item?.code) {
                    dictdict[item?.code] = item;
                } else {
                    console.log('No code for dictionary', item);
                }
            });
            return dictdict;
        }
        return data;
    } catch (error) {
        console.warn('Error fetching dictionary order:', error);
    }
    return {};
};

/**
 * TermDictionaries is original component by Gerard
 * Andres updated SOLR field names since then. Than created singular TermDictionary below based on this.
 * 2023-01-02
 * @param definitions
 * @returns {JSX.Element}
 * @constructor
 */
const TermDictionaries = ({ definitions, kmapData }) => {
    const { showDictionary, getDictInfo } = useDictionaryStore();
    const [dod, setDod] = useState(null);

    let {
        isLoading: isDictOrderLoading,
        data: dictOrderData,
        isError: isDictOrderError,
        error: dictOrderError,
    } = useQuery(
        ['kmaps', 'terms', 'dict', 'order', 'api'],
        () => getDictionaryOrder('list'),
        {
            staleTime: Infinity,
        }
    );

    if (isDictOrderLoading) {
        return null;
    }

    if (dictOrderData) {
        window.localStorage.setItem(
            'TermDictOrderData',
            JSON.stringify(dictOrderData)
        );
    }

    // const dictVar = calcDictVariants(kmapData);

    // Once order is loaded get list of dictionary titles in order, filtered for existing defintitions
    let dictOrder = Object.keys(definitions);

    if (!dictOrderData || Object.keys(dictOrderData).length === 0) {
        const storedDictOrder =
            window.localStorage.getItem('TermDictOrderData');
        if (dictOrderData) {
            dictOrderData = dictOrderData;
        }
    }

    if (Array.isArray(dictOrderData)) {
        dictOrder = dictOrderData
            .map((d, di) => {
                return d?.code;
            })
            .filter((dt, dti) => {
                return Object.keys(definitions).includes(dt); // only keep dictionary titles if their def exists for this term
            });
    }

    // Just in case the order doesn't load or something goes wrong, use the order of the definitions given
    if (!dictOrder || !Array.isArray(dictOrder) || dictOrder?.length === 0) {
        dictOrder = Object.keys(definitions);
    }

    const relatedToDefs = kmapData._childDocuments_.filter((c, ci) => {
        return (
            c?.block_child_type === 'related_terms' &&
            c?.related_terms_relation_code_s === 'is.related.to'
        );
    });

    const defOrder = dictOrder.filter((k, i) => {
        const showdict = kmapData?.name_tibt ? true : showDictionary(k);
        return Object.keys(definitions).includes(k) && showdict;
    });

    const origScript =
        kmapData?.name_deva?.length > 0
            ? kmapData?.name_deva[0]
            : kmapData?.name_tibt?.length > 0
            ? kmapData.name_tibt[0]
            : '';
    // dictOrder is a list of definition keys by dictionary name
    const dictsrcs = getDictSources(); // Gets the Mandala Source ID number for all dictionaries, from .env file
    // console.log("dictOrderData", dictOrderData);
    return (
        <div className="sui-termDicts__wrapper">
            <div className="sui-termDicts__content">
                {defOrder?.map((key, i) => {
                    let thedef = definitions[key]; // definitions are arrays bec. dicts might have more than one
                    let thedict = dictOrderData?.filter((d, di) => {
                        return d?.code === key;
                    });
                    if (thedict?.length > 0) {
                        thedict = thedict[0];
                    }
                    const dicttitle = dictTitleFix(thedict?.title);
                    const biblicon =
                        process.env.REACT_APP_DICT_SOURCES.includes(
                            thedict.code
                        ) ? (
                            <TermDictionarySource
                                srcid={dictsrcs[thedict.code]}
                                dict={thedict}
                            />
                        ) : null;

                    if (key === 'gualberto' && thedef?.length === 2) {
                        // console.log("the def", thedef);
                        return (
                            <div className={`dict-defs ${key}`} key={key}>
                                <div className="sui-termDicts__dict-name">
                                    {i + 1}. <span>{dicttitle}</span> {biblicon}
                                </div>
                                {/* <TermsVariant value={dictVar[key]} /> */}
                                <TermEtymologies data={kmapData} dkey={key} />
                                <RelatedTermsDictionary
                                    dict={key}
                                    docs={relatedToDefs}
                                />
                                <TermDictSpecialList type={key} def={thedef} />
                            </div>
                        );
                    }
                    /*
                    const grouped = thedef.reduce((acc, item) => {
                        console.log('item', item, item.related_definitions_language_s);
                        const key = item.related_definitions_language_s;
                        if (!acc[key]) acc[key] = [];
                        acc[key].push(item);
                        return acc;
                    }, {});
                    const hasMultiple = Object.values(grouped).some(group => group.length > 1);

                     */
                    thedef.sort((a, b) =>
                        b?.related_definitions_language_code_s?.localeCompare(
                            a?.related_definitions_language_code_s
                        )
                    );
                    return (
                        <div className={`dict-defs ${key}`} key={key}>
                            <div className="sui-termDicts__dict-name">
                                {i + 1}. <span>{dicttitle}</span> {biblicon}
                            </div>
                            {/* <TermsVariant value={dictVar[key]} /> */}
                            <TermEtymologies data={kmapData} dkey={key} />
                            <RelatedTermsDictionary
                                dict={key}
                                docs={relatedToDefs}
                            />
                            <ul className="sui-termDicts__dict-wrapper">
                                {thedef.map((def) => {
                                    let defcont = getDefContent(def).replace(
                                        /(<p[^>]+?>|<p>|<\/p>)/gim,
                                        ''
                                    );
                                    let langclass = getLangClass(
                                        def.related_definitions_language_s
                                    );
                                    if (
                                        langclass === 'sa' &&
                                        def.related_definitions_source_code_s ===
                                            'negi'
                                    ) {
                                        langclass = 'sa-ltn';
                                    }

                                    if (containsTibetan(defcont)) {
                                        defcont = markTibetan(defcont);
                                    }
                                    if (containsEnglish(defcont)) {
                                        defcont =
                                            '<span class="en">' +
                                            defcont +
                                            '</span>';
                                    }
                                    let defmu = (
                                        <div
                                            className={
                                                'sui-termDicts__dict-def ' +
                                                langclass
                                            }
                                        >
                                            {ReactHtmlParser(
                                                markTibetan(
                                                    convertHiToHTML(
                                                        escapeStrayAngleBrackets(
                                                            defcont
                                                        )
                                                    ),
                                                    '',
                                                    false,
                                                    true
                                                )
                                            )}
                                            <TermExtraInfo def={def} />
                                        </div>
                                    );

                                    // Variants and related terms (if def.related_uid_s is an Array, map it to use
                                    // GlossaryTermDisplay for each item. Otherwise, just use the defs.related_uid_s
                                    // Other code in array map adds commas between element, except and at the end
                                    if (!def.id.includes('definitions')) {
                                        defmu = (
                                            <div>
                                                <span
                                                    className={getLangCodeForString(
                                                        origScript
                                                    )}
                                                >
                                                    {origScript}{' '}
                                                </span>
                                                {
                                                    def?.related_terms_relation_label_s
                                                }
                                                {(Array.isArray(
                                                    def?.related_uid_s
                                                ) &&
                                                    def?.related_uid_s.map(
                                                        (ruis, ruisi) => {
                                                            return (
                                                                <React.Fragment
                                                                    key={
                                                                        'ruisi-' +
                                                                        ruisi
                                                                    }
                                                                >
                                                                    <GlossaryTermDisplay
                                                                        kid={
                                                                            ruis
                                                                        }
                                                                    />
                                                                    {(ruisi ===
                                                                        def
                                                                            ?.related_uid_s
                                                                            .length -
                                                                            2 &&
                                                                        ' and ') ||
                                                                        (ruisi <
                                                                            def
                                                                                ?.related_uid_s
                                                                                .length -
                                                                                1 &&
                                                                            ', ')}
                                                                </React.Fragment>
                                                            );
                                                        }
                                                    )) || (
                                                    <GlossaryTermDisplay
                                                        kid={def?.related_uid_s}
                                                    />
                                                )}
                                            </div>
                                        );
                                    }

                                    return (
                                        <li
                                            className="sui-termDicts__dict"
                                            data-solrid={def.id}
                                            key={def.id}
                                        >
                                            {defmu}
                                        </li>
                                    );
                                })}
                            </ul>
                            {/*
                            <div className="sui-termDicts__dict-extra">
                                <span className="sui-termDicts__dict-extra-lang">
                                    Language:
                                </span>{' '}
                                <span className="sui-termDicts__dict-extra-lang-text ">
                                    {
                                        definitions[key][0]
                                            ?.related_definitions_language_s
                                    }
                                </span>
                            </div>*/}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

function getDefContent(subdoc) {
    const langsuff = getUniquePropIds(
        subdoc,
        /related_definitions_content_(\w+)/
    );
    if (langsuff.length > 0) {
        const fnm = 'related_definitions_content_' + langsuff[0];
        return subdoc[fnm];
    }
    return '';
}

// Function to show "Variants:" as in Classical vs Modern Newar
function TermsVariant({ value }) {
    if (!value) return null;
    // console.log('value', value);
    const [tname, tkid] = value.split(':');
    return (
        <div className="sui-termDicts__variants">
            <span className="font-weight-bold">Variant: </span> {tname}
            <MandalaPopover domain="subjects" kid={tkid} />
        </div>
    );
}

function TermDictSpecialList({ type, def }) {
    if (type === 'gualberto') {
        const def1cont = getDefContent(def[0]).replace(
            /(<p[^>]+?>|<p>|<\/p>)/gim,
            ''
        );
        const def2cont = getDefContent(def[1]).replace(
            /(<p[^>]+?>|<p>|<\/p>)/gim,
            ''
        );
        const langclass = getLangClass(def[0].related_definitions_language_s);
        return (
            <>
                <ul className="sui-termDicts__dict-wrapper">
                    <li
                        className="sui-termDicts__dict"
                        data-solrid={def[0].id}
                        key={def[0].id}
                    >
                        <div className={'sui-termDicts__dict-def ' + langclass}>
                            {ReactHtmlParser(def1cont)} [
                            {ReactHtmlParser(def2cont)}]
                            <TermExtraInfo def={def[0]} />
                        </div>
                    </li>
                </ul>
            </>
        );
    }
}

function RelatedTermsDictionary({ dict, docs }) {
    const relations = docs?.filter((d, di) => {
        return d?.related_terms_relation_source_s === dict;
    });
    if (relations?.length > 0) {
        const combrels = {};
        relations.map((r, ri) => {
            const combkey = r?.related_terms_relation_label_s;
            if (!Object.keys(combrels).includes(combkey)) {
                combrels[combkey] = [];
            }
            let lkn = r?.related_terms_id_s?.replace('-', '/');
            if (!lkn?.length > 0) {
                lkn = '#';
            } else {
                lkn = '/' + lkn;
            }
            combrels[combkey].push(
                <Link to={lkn} key={`${dict}-relterm-${ri}`}>
                    {r?.related_terms_header_s}
                </Link>
            );
        });
        return (
            <dl>
                {Object.keys(combrels).map((k, ki) => {
                    const reactkey = `${dict}-${k}-relterm-${ki}`;

                    return (
                        <React.Fragment key={reactkey}>
                            <dt>{k}</dt>
                            <dd>
                                {combrels[k].map((term, ti) => {
                                    const suffix =
                                        ti < combrels[k].length - 1 ? (
                                            ', '
                                        ) : (
                                            <br />
                                        );
                                    return (
                                        <span key={`term-${ki}-${ti}`}>
                                            {term}
                                            {suffix}
                                        </span>
                                    );
                                })}
                            </dd>
                        </React.Fragment>
                    );
                })}
            </dl>
        );
    }
    return null;
}

function TermDefLang({ def }) {
    return (
        <div className="sui-termDicts__dict-lang">
            <span className="sui-termDicts__dict-lang-label">Language:</span>{' '}
            <span className="sui-termDicts__dict-lang-text ">
                {def?.related_definitions_language_s}
            </span>
        </div>
    );
}

function TermExtraInfo({ def }) {
    const [isOpen, setIsOpen] = useState(false);

    const toggleCollapse = () => {
        setIsOpen(!isOpen);
    };
    const branchsubjs = getRelatedProps(
        def,
        'related_definitions_branch_subjects-'
    );
    /*
    if (def.id.includes('terms-346232')) {
        console.log('def', def);
        console.log('branchsubjs', branchsubjs);
    }
     */
    // console.log("Defin in esxtra", def);

    return (
        <div className="sui-termDicts__further-info">
            <div className="info-link">
                <a onClick={toggleCollapse} title="Further Details">
                    <BsInfoCircle />
                    <span className="align-middle"> </span>
                </a>
            </div>
            <div className={!isOpen ? 'collapse' : 'info'}>
                <TermDefLang def={def} />
                {/* branch subjects */}
                {Object.keys(branchsubjs).map((key) => {
                    const bs = branchsubjs[key];
                    const headers = Array.isArray(bs.subjects_headers_t)
                        ? bs.subjects_headers_t.join(', ')
                        : bs.subjects_headers_t;
                    return (
                        <div key={key}>
                            <span>
                                <strong>{bs.header_s}</strong>: {headers}
                            </span>
                        </div>
                    );
                })}

                {/* model sentences */}
                <TermModelSentences def={def} />
                <TermNotes def={def} />
                <TermEtymologies data={def} isdef={true} />
            </div>
        </div>
    );
}
/*
function TermDefBranchSubjects({ def }) {
    //related_definitions_branch_subjects
    // console.log('def', def);
    const branchsubjs = getRelatedProps(
        def,
        'related_definitions_branch_subjects-'
    );
    if (def.id === 'terms-21124_definitions-3514') {
        console.log('branchsubjs', branchsubjs);
    }
    if (!branchsubjs) {
        return null;
    }
    const result = Object.keys(branchsubjs).map((key) => {
        const bs = branchsubjs[key];
        return (
            <span key={key} className="font-95">
                <strong>{bs.header_s}</strong>: {bs.subjects_headers_t}
            </span>
        );
    });
    return result;
}

 */

function TermModelSentences({ def }) {
    // Model Sentences
    const models = getRelatedProps(
        def,
        'related_definitions_model_sentence_',
        '_'
    );

    const modelsout = [];
    for (let mid in models) {
        const mdata = models[mid];
        for (let prop in mdata) {
            const mval = mdata[prop].replace(/(<p[^>]+?>|<p>|<\/p>)/gim, '');
            const label = prop.includes('translation')
                ? 'Translation'
                : 'Model Sentence';
            modelsout.push(
                <div key={[mid, prop]} className={`sui-termDicts__dict-model`}>
                    <strong className="font-95">{label}</strong>: {mval}
                </div>
            );
        }
    }
    return modelsout;

    /* old code
    // model sentences
    const models = getRelatedProps(
        def,
        'related_definitions_model_sentence_',
        '_'
    );

    if (def.id === 'terms-21124_definitions-3514') {
        console.log('models', models);
    }
    if (!models || Object.keys(models).length === 0) {
        return null;
    }
    const output = [];
    for (let mid in models) {
        const mdata = models[mid];
        for (let prop in mdata) {
            const mval = mdata[prop].replace(/(<p[^>]+?>|<p>|<\/p>)/gim, '');
            const label = prop.includes('translation')
                ? 'Translation'
                : 'Model Sentence';
            const bsp = prop.includes('translation') ? 'mb-2' : '';
            output.push(
                <div
                    key={[mid, prop]}
                    className={`sui-termDicts__dict-model ${bsp}`}
                >
                    <strong className="font-95">{label}</strong>: {mval}
                </div>
            );
        }
    }
    return <>{output}</>;

     */
}

// Notes
function TermNotes({ def }) {
    const notes = getRelatedProps(def, 'related_definitions_note_', '_');
    let noteels = null;
    if (Object.keys(notes).length > 0) {
        noteels = [];
        for (let prop in notes) {
            noteels.push(
                <div key={prop} className="sui-termDicts__dict-note">
                    <strong>Note: </strong> {notes[prop].content_t}
                </div>
            );
        }
    }
    return noteels;
}

function TermEtymologies({ data, dkey = '', isdef = false }) {
    let etymology = null;
    if (isdef) {
        etymology = data['related_definitions_etymologies_ss'];
    } else {
        const props = getRelatedProps(data, 'etymology_');
        if (data.id === 'terms-247940') {
            for (let prop in props) {
                const propval = props[prop];
                if (propval['source_code_s'] === dkey) {
                    etymology = propval['content_s'];
                }
            }
        }
    }
    if (!etymology) {
        return null;
    }
    return (
        <div className="sui-termDicts-etymology">
            <span className="font-weight-bold">Etymology: </span> {etymology}
        </div>
    );
}

export default TermDictionaries;
